// ============================================================
// ENGINE — ROOM CONSOLE (Gilded Obsidian)
// ============================================================
// The room's chrome, split into TWO independent pill-framed pieces
// (caP'n's call, July 8 — "pills, not giant rectangles"; the same
// pill-run discipline we did across Terror):
//
//   ┌─ THE RECORD ─────────┐   ← collapsible session log. Header is
//   │ ◆ THE RECORD      ▾ │     a pill you can tap to fold the whole
//   ├──────────────────────┤     log away so you're not staring at a
//   │  willy ◆ ...          │     giant chat wall. Lives at the TOP of
//   │  kiosk ◆ ...          │     the right letterbox column.
//   └──────────────────────┘
//
//        ┌ ◆ SPEAK — WILLY · Q ┐  ← the interaction PROMPT, now its
//        └─────────────────────┘     OWN tiny pill, floating free —
//                                     no longer welded to the Record.
//                                     Sits low in the right column.
//
// WHY THE SPLIT
//   The Record (a persistent log) and the interaction prompt (a
//   transient "press Q here") are different lifetimes and different
//   jobs. Fusing them made one giant rectangle that was always tall
//   and always on. Split + collapsible = two small pills that each
//   show only when they have something to say.
//
// LAYOUT MODES
//   • desktop (default) — both pieces stack in the RIGHT letterbox
//                         column. Record pinned top; prompt pill low.
//   • mobile            — horizontal prompt bar above the D-pad; the
//                         log collapses into the same bar (unchanged
//                         footprint, still small).
//   • inline            — position:relative block for the Plaza
//                         portrait DockPortal.
//
// Purely presentational — the ROOM owns all state. The only thing
// that lives here is the session-log store + the collapse flag
// (module-level so a fold survives leaving/re-entering a room).
// GM chrome only. No keyframes needed.
// ============================================================

import { useEffect, useRef, useState } from "react";
import { GM } from "./theme";

// ── SESSION LOG STORE ─────────────────────────────────────────
// Module-level, keyed by zoneId. Survives unmount/remount for
// the whole browser session (intentional — NOT the stale-boolean
// keyframe trap; this is data, not DOM-presence tracking).
const SESSION_LOGS = {};

export function getSessionLog(zoneId) {
  return SESSION_LOGS[zoneId] || [];
}

export function setSessionLog(zoneId, entries) {
  SESSION_LOGS[zoneId] = entries;
}

// Collapse flag store — remembers whether the Record is folded, per
// zone, for the session. Same rationale as the log store.
const RECORD_COLLAPSED = {};

// ── SHARED PILL CHROME ────────────────────────────────────────
// One obsidian pill recipe both pieces use, so the look is identical.
const PILL_BG = "linear-gradient(175deg, rgba(5,3,12,0.95) 0%, rgba(13,9,24,0.92) 100%)";
const pillFrame = (extra = {}) => ({
  background: PILL_BG,
  border: `1px solid ${GM.pageBorder}`,
  borderRadius: 999,          // full pill by default; overridden for the log body
  boxShadow: "0 8px 26px rgba(0,0,0,0.65), inset 0 1px 0 rgba(226,232,244,0.08)",
  fontFamily: GM.font,
  ...extra,
});

// ─────────────────────────────────────────────────────────────
// THE RECORD — collapsible session-log pill.
// Header is always a pill; when open, a rounded log body drops
// below it. Tap the header to fold/unfold.
// ─────────────────────────────────────────────────────────────
function RecordPanel({ log, zoneId, mode }) {
  const [collapsed, setCollapsed] = useState(
    () => RECORD_COLLAPSED[zoneId] ?? false
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length, collapsed]);

  const toggle = () => {
    setCollapsed(c => {
      const next = !c;
      RECORD_COLLAPSED[zoneId] = next;
      return next;
    });
  };

  if (log.length === 0) return null;

  const right = mode === "right";

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      gap: 6, minHeight: 0,
      ...(right ? { flex: collapsed ? "0 0 auto" : 1 } : {}),
      width: "100%",
    }}>
      {/* Header pill — the tap target */}
      <button
        onClick={toggle}
        style={{
          ...pillFrame(),
          flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, padding: "7px 16px", cursor: "pointer",
          width: "100%",
        }}
      >
        <span style={{
          fontSize: 8.5, letterSpacing: 3, fontWeight: 900,
          color: `${GM.inkGold}99`,
        }}>◆ THE RECORD</span>
        <span style={{
          fontSize: 10, color: GM.vellumDim, fontWeight: 900,
          transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
          transition: "transform 0.18s ease",
        }}>▾</span>
      </button>

      {/* Log body — only when open. Rounded rectangle (not a pill —
          it holds scrolling text) sharing the same obsidian recipe. */}
      {!collapsed && (
        <div
          ref={scrollRef}
          style={{
            ...pillFrame({ borderRadius: 14 }),
            ...(right ? { flex: 1, minHeight: 0 } : { maxHeight: 138 }),
            overflowY: "auto",
            padding: "10px 14px 8px",
            display: "flex", flexDirection: "column", gap: 6,
          }}
        >
          {log.map((e, i) => (
            <div key={i} style={{ fontSize: 11.5, lineHeight: 1.6 }}>
              <span style={{
                color: e.color || GM.inkGold, fontWeight: 900,
                letterSpacing: 1.5, fontSize: 9,
              }}>
                {(e.label || "").toUpperCase()} ◆{" "}
              </span>
              <span style={{ color: GM.vellum, fontStyle: "italic" }}>{e.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// THE PROMPT PILL — the transient interaction / dialogue control.
// Its own tiny pill; shows only when there's a target or an active
// dialogue. Carries the ◀ ▶ target cycler inline when needed.
// ─────────────────────────────────────────────────────────────
function PromptPill({
  dialogue, onAdvance,
  cur, many, nearbyIndex, nearbyList, onCycle, onActivate,
  mobile,
}) {
  const hasMore = dialogue ? dialogue.idx < dialogue.lines.length - 1 : false;

  const arrowStyle = {
    pointerEvents: "all", cursor: "pointer",
    background: "linear-gradient(175deg, #1e1636 0%, #150e26 55%, #0c0818 100%)",
    border: `1px solid ${GM.pageBorder}`,
    borderRadius: "50%", color: GM.inkGoldBright,
    width: 26, height: 26, fontSize: 11, fontFamily: GM.font,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "inset 0 1px 0 rgba(226,232,244,0.12)",
    flexShrink: 0,
  };

  // Active dialogue → a single continue/close pill
  if (dialogue) {
    return (
      <div style={{
        ...pillFrame(),
        alignSelf: "center",
        display: "flex", justifyContent: "center",
        padding: "6px 8px", pointerEvents: "none",
      }}>
        <button onClick={onAdvance} style={{
          pointerEvents: "all", cursor: "pointer",
          background: "transparent",
          border: `1px solid ${(dialogue.color || GM.inkGold)}66`,
          borderRadius: 999, padding: "6px 22px",
          color: dialogue.color || GM.inkGold,
          fontFamily: GM.font, fontSize: 10, fontWeight: 900, letterSpacing: 2,
        }}>
          {hasMore ? "▸ CONTINUE" : "✕ CLOSE"}{!mobile && " · Q"}
        </button>
      </div>
    );
  }

  if (!cur) return null;

  // Interaction prompt → a compact pill wrapping just the words
  return (
    <div style={{
      ...pillFrame(),
      alignSelf: "center",
      display: "inline-flex", alignItems: "center",
      gap: 8, padding: "5px 10px",
      maxWidth: "100%",
    }}>
      {many && (
        <button onClick={() => onCycle && onCycle(-1)} style={arrowStyle}>◀</button>
      )}

      <button onClick={onActivate} style={{
        pointerEvents: "all", cursor: "pointer",
        background: "transparent", border: "none",
        color: cur.color || GM.inkGoldBright,
        fontFamily: GM.font, fontSize: mobile ? 10 : 11,
        fontWeight: 900, letterSpacing: 2,
        textShadow: `0 0 8px ${cur.color || GM.inkGoldBright}44`,
        padding: "4px 6px", minWidth: 0,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        ◆ {cur.verb.toUpperCase()} — {cur.label.toUpperCase()}
        {cur.comingSoon && <span style={{ color: GM.vellumFaint }}> (SOON)</span>}
        {!mobile && <span style={{ color: GM.vellumDim, fontWeight: 400 }}> · Q</span>}
      </button>

      {many && (
        <button onClick={() => onCycle && onCycle(1)} style={arrowStyle}>▶</button>
      )}

      {many && (
        <span style={{
          fontSize: 8.5, color: GM.vellumFaint, letterSpacing: 1, flexShrink: 0,
        }}>
          {nearbyIndex + 1}/{nearbyList.length}
        </span>
      )}
    </div>
  );
}

// ── CONSOLE ───────────────────────────────────────────────────
export default function RoomConsole({
  mobile = false,
  inline = false,
  zoneId = "room",       // for the per-room collapse memory
  topOffset = 64,        // desktop: clear the room HUD strip
  log = [],              // [{ label, color, text }]
  dialogue = null,       // { lines, color, label, idx } | null
  onAdvance,             // advance/close active dialogue
  nearbyList = [],       // interactables currently in range
  nearbyIndex = 0,       // which one is selected
  onCycle,               // (dir) => cycle selection
  onActivate,            // activate current selection
}) {
  const visible = !!dialogue || nearbyList.length > 0 || log.length > 0;
  if (!visible) return null;

  const cur = nearbyList[nearbyIndex] || null;
  const many = nearbyList.length > 1;
  const rightColumn = !mobile && !inline;

  const record = (
    <RecordPanel log={log} zoneId={zoneId} mode={rightColumn ? "right" : "inline"} />
  );
  const prompt = (
    <PromptPill
      dialogue={dialogue} onAdvance={onAdvance}
      cur={cur} many={many}
      nearbyIndex={nearbyIndex} nearbyList={nearbyList}
      onCycle={onCycle} onActivate={onActivate}
      mobile={mobile}
    />
  );

  // ── DESKTOP: right letterbox column. Record pinned top (grows to
  //    fill), prompt pill floats at the bottom of the column. Two
  //    separate pills, gap between them. Container is click-through;
  //    the pills themselves capture their own clicks. ──
  if (rightColumn) {
    return (
      <div style={{
        position: "fixed", right: 12, top: topOffset, bottom: 14, zIndex: 60,
        width: "min(280px, 24vw)",
        display: "flex", flexDirection: "column",
        gap: 10, pointerEvents: "none",
      }}>
        <div style={{ pointerEvents: "auto", display: "flex", flexDirection: "column", minHeight: 0, flex: log.length ? 1 : "0 0 auto" }}>
          {record}
        </div>
        <div style={{ flex: log.length ? "0 0 auto" : 1 }} />
        <div style={{ pointerEvents: "auto", display: "flex", justifyContent: "center" }}>
          {prompt}
        </div>
      </div>
    );
  }

  // ── INLINE (portrait dock): stack, no fixed positioning. ──
  if (inline) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
        {record}
        <div style={{ display: "flex", justifyContent: "center" }}>{prompt}</div>
      </div>
    );
  }

  // ── MOBILE: compact stack above the D-pad. Record collapsible,
  //    prompt pill below it, both centered and small. ──
  return (
    <div style={{
      position: "fixed", left: "50%", transform: "translateX(-50%)",
      bottom: 118, zIndex: 60, width: "min(560px, 94vw)",
      display: "flex", flexDirection: "column", gap: 8,
      alignItems: "center", pointerEvents: "none",
    }}>
      <div style={{ pointerEvents: "auto", width: "100%" }}>{record}</div>
      <div style={{ pointerEvents: "auto" }}>{prompt}</div>
    </div>
  );
}
